import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import youtubeService from '../Services/youtubeService';
import { FaArrowLeft, FaEye } from 'react-icons/fa';

const VideoDetail = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFullDescription, setShowFullDescription] = useState(false);

  useEffect(() => {
    const fetchVideoDetails = async () => {
      try {
        setLoading(true);
        // For now, we'll use mock data since we don't have individual video fetching
        // In a real implementation, you'd fetch video details by ID
        const mockVideo = {
          id: videoId,
          title: 'Morning Aarti - Live from Temple',
          description: 'Join us for the beautiful morning aarti ceremony. Experience the divine energy and spiritual vibrations as we offer prayers to the deities. This sacred ritual has been performed for centuries and continues to bring peace and spiritual fulfillment to devotees around the world. The ceremony includes traditional chants, offerings, and blessings that connect us with the divine presence.',
          channelTitle: 'Spiritual Temple',
          viewCount: '1250',
          isLive: true,
          thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
        };
        setVideo(mockVideo);
      } catch (error) {
        console.error('Error fetching video details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (videoId) {
      fetchVideoDetails();
    }
  }, [videoId]);

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  const formatViewCount = (count) => {
    const num = parseInt(count);
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loader mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Video Not Found</h2>
          <button
            onClick={() => navigate('/live')}
            className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors"
          >
            {t('backToLive')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b pt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/live')}
            className="flex items-center gap-2 text-gray-600 hover:text-orange-500 transition-colors mb-4"
          >
            <FaArrowLeft />
            <span>{t('backToLive')}</span>
          </button>
          
          <div className="flex items-center gap-3">
            {video.isLive && (
              <div className="flex items-center gap-2 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                {t('live')}
              </div>
            )}
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              {video.title}
            </h1>
          </div>
          
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
            <span>{video.channelTitle}</span>
            <div className="flex items-center gap-1">
              <FaEye />
              <span>{formatViewCount(video.viewCount)} {t('views')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Video Player */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={youtubeService.getVideoEmbedUrl(videoId)}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>

            {/* Video Description */}
            <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('description')}
              </h2>
              <div className="text-gray-600 leading-relaxed">
                {showFullDescription ? (
                  <p>{video.description}</p>
                ) : (
                  <p>
                    {video.description.length > 200 
                      ? `${video.description.substring(0, 200)}...` 
                      : video.description
                    }
                  </p>
                )}
                {video.description.length > 200 && (
                  <button
                    onClick={toggleDescription}
                    className="text-orange-500 hover:text-orange-600 font-medium mt-2"
                  >
                    {showFullDescription ? t('readLess') : t('readMore')}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('videoInfo')}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">{t('channel')}</h4>
                  <p className="text-gray-600">{video.channelTitle}</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900">{t('views')}</h4>
                  <p className="text-gray-600">{formatViewCount(video.viewCount)}</p>
                </div>
                
                {video.isLive && (
                  <div>
                    <h4 className="font-medium text-gray-900">{t('status')}</h4>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-red-500 font-medium">{t('live')}</span>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoDetail;
